import React from 'react'
import { Link } from 'react-router-dom'
import { useCMSCollection } from '../hooks/useCMS'

export function ForloebHighlight() {
  const { data: cmsForloeb } = useCMSCollection('forloeb')
  const sortedSteps = Array.isArray(cmsForloeb) && cmsForloeb.length > 0
    ? [...cmsForloeb].sort((a, b) => (a.step_number || 0) - (b.step_number || 0))
    : null
  return (
    <section
      className="py-16 sm:py-20 lg:py-24 text-white relative overflow-hidden bg-cover bg-center bg-no-repeat bg-fixed"
      style={{
        backgroundImage: `url('https://assets.auxodev.dk/assets/2a253654-8b0d-44c3-813e-14453c64ff7c/20170831-o2q2440.jpg')`,
      }}
    >
      {/* Dark transparent overlay */}
      <div className="absolute inset-0 bg-[#132835]/80 pointer-events-none" aria-hidden="true" />

      {/* Decorative ambient backdrop */}
      <div className="absolute top-0 right-0 w-96 h-96 bg-[#7c875a]/10 rounded-full blur-3xl pointer-events-none -mr-20 -mt-20" />
      <div className="absolute bottom-0 left-0 w-80 h-80 bg-[#2c556b]/20 rounded-full blur-3xl pointer-events-none -ml-20 -mb-20" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 relative z-10">
        <div className="bg-white/5 border border-white/10 rounded-3xl p-8 sm:p-12 lg:p-16 backdrop-blur-sm">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
            {/* Left Content (Span 7) */}
            <div className="lg:col-span-7 space-y-6">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#7c875a]/20 text-[#7c875a] text-xs font-semibold uppercase tracking-wider">
                <i className="fa-solid fa-layer-group text-xs" aria-hidden="true" />
                <span>En helhedsorienteret proces</span>
              </div>
              <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-white leading-tight">
                Forløb — ikke enkeltbehandlinger
              </h2>
              <div className="space-y-4 text-sm sm:text-base text-[#FAF8F5]/85 leading-relaxed">
                <p className="font-medium text-white text-base sm:text-lg">
                  Jeg tilbyder behandlingsforløb, og kun enkeltbehandlinger som opfølgning på et gennemført forløb.
                </p>
                <p>
                  Årsagen til dette er, at jeg brænder for at hjælpe dig med at arbejde i dybden og finde ind til de underliggende årsager, der forårsager symptomerne — frem for kun at give en kortvarig symptomlindring.
                </p>
                <p className="text-sm text-[#FAF8F5]/70">
                  Uanset om du kæmper med stress, følelsesmæssige blokeringer, spændinger, søvnbesvær eller angst, kræver en varig forandring tryghed, tillid og en sammenhængende proces.
                </p>
              </div>

              {/* Action buttons stacked on mobile, inline on sm+ */}
              <div className="pt-2 flex flex-col sm:flex-row items-stretch sm:items-center gap-3 sm:gap-4">
                <Link
                  to="/forloeb"
                  className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-[#7c875a] hover:bg-[#6a744c] text-[#132835] font-semibold px-6 py-3.5 rounded-full text-sm transition-all shadow-md hover:shadow text-center"
                >
                  <span>Sådan foregår et forløb</span>
                  <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true" />
                </Link>
                <Link
                  to="/kontakt"
                  className="w-full sm:w-auto inline-flex items-center justify-center gap-2 border border-white/30 hover:border-white text-white font-medium px-6 py-3.5 rounded-full text-sm transition-all hover:bg-white/5 text-center"
                >
                  <span>Samtale om dit behov</span>
                </Link>
              </div>
            </div>

            {/* Right Feature Card (Span 5) */}
            <div className="lg:col-span-5 bg-white/10 rounded-2xl p-6 sm:p-8 border border-white/15 space-y-6">
              <h3 className="font-serif text-xl font-bold text-white flex items-center gap-2">
                <i className="fa-solid fa-compass text-[#7c875a] text-base" aria-hidden="true" />
                <span>Rejsen gennem dit forløb</span>
              </h3>
              <ul className="space-y-4 text-sm text-[#FAF8F5]/90">
                {sortedSteps ? (
                  sortedSteps.map((step, idx) => (
                    <li key={idx} className="flex items-start gap-3">
                      <div className="w-6 h-6 rounded-full bg-[#7c875a] text-[#132835] flex items-center justify-center text-xs font-bold flex-shrink-0 mt-0.5">
                        {step.step_number || idx + 1}
                      </div>
                      <div>
                        <strong className="text-white block font-medium">{step.title}</strong>
                        <span className="text-xs text-[#FAF8F5]/70">
                          {step.description}
                        </span>
                      </div>
                    </li>
                  ))
                ) : (
                  <>
                    <li className="flex items-start gap-3">
                      <div className="w-6 h-6 rounded-full bg-[#7c875a] text-[#132835] flex items-center justify-center text-xs font-bold flex-shrink-0 mt-0.5">
                        1
                      </div>
                      <div>
                        <strong className="text-white block font-medium">Spørgeskema &amp; forberedelse</strong>
                        <span className="text-xs text-[#FAF8F5]/70">
                          Problemspecifik kortlægning før første session.
                        </span>
                      </div>
                    </li>
                    <li className="flex items-start gap-3">
                      <div className="w-6 h-6 rounded-full bg-[#7c875a] text-[#132835] flex items-center justify-center text-xs font-bold flex-shrink-0 mt-0.5">
                        2
                      </div>
                      <div>
                        <strong className="text-white block font-medium">Grundig forundersøgelse</strong>
                        <span className="text-xs text-[#FAF8F5]/70">
                          Samtale og fysisk undersøgelse på briksen.
                        </span>
                      </div>
                    </li>
                    <li className="flex items-start gap-3">
                      <div className="w-6 h-6 rounded-full bg-[#7c875a] text-[#132835] flex items-center justify-center text-xs font-bold flex-shrink-0 mt-0.5">
                        3
                      </div>
                      <div>
                        <strong className="text-white block font-medium">Skræddersyet forløb</strong>
                        <span className="text-xs text-[#FAF8F5]/70">
                          Kropsterapi, coaching og/eller hypnose tilpasset dit tempo.
                        </span>
                      </div>
                    </li>
                    <li className="flex items-start gap-3">
                      <div className="w-6 h-6 rounded-full bg-[#7c875a] text-[#132835] flex items-center justify-center text-xs font-bold flex-shrink-0 mt-0.5">
                        4
                      </div>
                      <div>
                        <strong className="text-white block font-medium">Integration i hverdagen</strong>
                        <span className="text-xs text-[#FAF8F5]/70">
                          Værktøjer og nye indsigter, der holder på lang sigt.
                        </span>
                      </div>
                    </li>
                  </>
                )}
              </ul>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}
